import { yupResolver } from "@hookform/resolvers/yup"
import { useState } from "react"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { useCustomerPageContext } from "@contexts/customer"
import { updateExtendReducePlan } from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import { Stack } from "@mui/material"
import { formatDate } from "@utils/functions/formatDate"
import { showErrorToast } from "@utils/toast"
import { ExtendOrReduce } from "__generated__/graphql"
import { useWatch } from "react-hook-form"

export type ExtendReduceFormData = {
  type: string
  days: number
  currentFreeTrialExpiringDate?: string
  nextFreeTrialExpiringDate?: string
  endDate?: string
  nextEndDate?: string
  refreshDate?: string
  nextRefreshDate?: string
}

export default function ExtendReduceDialog({
  isOpen,
  setClose
}: {
  isOpen: boolean
  setClose: () => void
}) {
  const { translate, currentCustomer: customer } = useCustomerPageContext()

  const schema = yup
    .object({
      type: yup.string().required(translate("type_field_is_required")),
      days: yup
        .number()
        .required(translate("days_field_is_required"))
        .min(1, translate("days_must_be_at_least_1"))
        .max(365, translate("days_must_be_maximum_365"))
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<ExtendReduceFormData>({
    resolver: yupResolver(schema),
    mode: "all"
  })

  const [loading, setLoading] = useState(false)

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }

  async function onSubmit(data: ExtendReduceFormData) {
    if (!customer?._id) {
      return
    }
    setLoading(true)
    const { isSuccess, errorMessage } = await updateExtendReducePlan({
      customerId: customer?._id,
      days: data.days,
      extendOrReduce: data.type as ExtendOrReduce
    })
    if (!isSuccess && !!errorMessage) {
      showErrorToast(String(errorMessage))
    }
    setLoading(false)
    handleClose()
  }

  const extendOrReduceType = useWatch({ control, name: "type" })
  const days = useWatch({ control, name: "days" })

  function getShiftedDate(dateValue?: string | null) {
    if (!dateValue || !days || !extendOrReduceType) {
      return undefined
    }

    const parsedDate = new Date(dateValue)

    if (Number.isNaN(parsedDate.getTime())) {
      return undefined
    }

    const shiftedDate = new Date(parsedDate)
    const dayChange =
      extendOrReduceType === "REDUCE" ? Number(days) * -1 : Number(days)

    shiftedDate.setDate(shiftedDate.getDate() + dayChange)

    return shiftedDate
  }

  function getNextRefreshDate(endDate?: Date) {
    if (!endDate) {
      return undefined
    }

    const refreshDate = new Date()
    const endDayOfMonth = endDate.getDate()

    if (endDate.getDay() > refreshDate.getDay()) {
      refreshDate.setDate(endDayOfMonth)
    } else {
      refreshDate.setMonth(refreshDate.getMonth() + 1)
      refreshDate.setDate(1)
      refreshDate.setDate(endDayOfMonth)
    }

    refreshDate.setHours(
      endDate.getHours(),
      endDate.getMinutes(),
      endDate.getSeconds(),
      0
    )

    return refreshDate
  }

  const currentEndDate = customer?.package?.endDate as string | undefined
  const currentFreeTrialExpiringDate = customer?.package
    ?.freeTrialExpiringDate as string | undefined
  const currentRefreshDate = (
    customer?.package as { refreshDate?: string } | undefined
  )?.refreshDate
  const hasFreeTrialExpiringDate = Boolean(currentFreeTrialExpiringDate)
  const nextEndDate = getShiftedDate(currentEndDate)
  const nextRefreshDate = getNextRefreshDate(nextEndDate)
  const nextFreeTrialExpiringDate = hasFreeTrialExpiringDate
    ? nextEndDate?.toISOString()
    : undefined

  function formatPreviewDate(dateValue?: string, emptyValue = "-") {
    if (!dateValue) {
      return emptyValue
    }

    return formatDate(translate("date_format"), dateValue)
  }

  return (
    <DialogProvider
      title={translate("extend_reduce")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<ExtendReduceFormData>
          type="select"
          label={translate("extend_or_reduce")}
          disabled={loading}
          name="type"
          placeholder={translate("modal_select_empty")}
          control={control}
          selectOptions={[
            { label: translate("extend"), value: "EXTEND" },
            { label: translate("reduce"), value: "REDUCE" }
          ]}
        />

        <TextInput<ExtendReduceFormData>
          type="number"
          label={translate("days")}
          disabled={loading}
          name="days"
          placeholder="0"
          control={control}
        />
        <TextInput<ExtendReduceFormData>
          label="Current free trial expire date"
          value={formatPreviewDate(currentFreeTrialExpiringDate, "")}
          name="currentFreeTrialExpiringDate"
          disabled
          placeholder=""
          control={control}
        />
        <TextInput<ExtendReduceFormData>
          label="Free trial expire date after change"
          value={formatPreviewDate(nextFreeTrialExpiringDate, "")}
          name="nextFreeTrialExpiringDate"
          disabled
          placeholder=""
          control={control}
        />
        <TextInput<ExtendReduceFormData>
          label="Current end date"
          value={formatPreviewDate(currentEndDate)}
          name="endDate"
          disabled
          placeholder="-"
          control={control}
        />
        <TextInput<ExtendReduceFormData>
          label="End date after change"
          value={formatPreviewDate(nextEndDate?.toISOString())}
          name="nextEndDate"
          disabled
          placeholder="-"
          control={control}
        />
        <TextInput<ExtendReduceFormData>
          label="Current refresh date"
          value={formatPreviewDate(currentRefreshDate)}
          name="refreshDate"
          disabled
          placeholder="-"
          control={control}
        />
        <TextInput<ExtendReduceFormData>
          label="Refresh date after change"
          value={formatPreviewDate(nextRefreshDate?.toISOString())}
          name="nextRefreshDate"
          disabled
          placeholder="-"
          control={control}
        />
      </Stack>
    </DialogProvider>
  )
}
